import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../contexts/CartContext';
import ChangePasswordForm from '../profile/ChangePasswordForm';

const Navbar = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const { user, logout, loading: authLoading, checkAuth } = useAuth();
    const { itemCount } = useCart();
    const history = useHistory();

    // Add scroll event listener to change navbar style when scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Create a custom event listener for auth changes
    useEffect(() => {
        let isMounted = true;
        let refreshTimeout: NodeJS.Timeout | null = null;

        // Renamed to avoid potential naming conflicts and for clarity
        const refreshAuthState = async () => {
            if (!isMounted) {
                // console.log('Navbar: Component unmounted, skipping auth state refresh.');
                return;
            }

            // console.log('Navbar: Auth refresh triggered. Current user:', user?.username || 'None');

            // Clear existing timeout to ensure only one refresh operation runs
            if (refreshTimeout) {
                clearTimeout(refreshTimeout);
            }

            refreshTimeout = setTimeout(async () => {
                if (!isMounted) {
                    // console.log('Navbar: Component unmounted before auth check execution.');
                    return;
                }
                try {
                    // console.log('Navbar: Executing checkAuth().');
                    // The checkAuth function from useAuth should handle all logic related to
                    // verifying the token and updating the authentication state.
                    await checkAuth();
                    // console.log('Navbar: checkAuth() executed. New user state should propagate from useAuth.');
                    // If useAuth updates its 'user' object correctly (with a new reference),
                    // Navbar will re-render automatically.
                } catch (error) {
                    // Check isMounted again in case of unmount during the async operation
                    if (!isMounted) {
                        // console.log('Navbar: Component unmounted during checkAuth error handling.');
                        return;
                    }
                    console.error('Navbar: Error during checkAuth() execution:', error);
                }
            }, 100); // Using a 100ms delay. The original 300ms might be safer if there are known race conditions
                     // with token storage. If issues persist, or if this delay causes problems,
                     // this value may need adjustment (e.g., back to 300ms or even 0 if the event
                     // dispatch is guaranteed to be after all state is settled).
        };

        // Renamed for clarity
        const handleAuthChangeEvent = () => {
            // console.log('Navbar: "auth-state-changed" event received.');
            refreshAuthState();
        };

        // Initial auth check on component mount
        // console.log('Navbar: Performing initial auth check on mount.');
        refreshAuthState();

        // Add event listener for auth state changes
        window.addEventListener('auth-state-changed', handleAuthChangeEvent);
        // console.log('Navbar: "auth-state-changed" event listener added.');

        // Cleanup function
        return () => {
            isMounted = false;
            window.removeEventListener('auth-state-changed', handleAuthChangeEvent);
            // console.log('Navbar: "auth-state-changed" event listener removed.');
            if (refreshTimeout) {
                clearTimeout(refreshTimeout);
                // console.log('Navbar: Auth refresh timeout cleared on unmount.');
            }
        };
    }, [checkAuth]); // Dependency on checkAuth: if the function reference from useAuth changes,
                      // this effect will re-run. 'user' is not added here as this effect
                      // is responsible for *triggering* user updates, not reacting to them by re-binding.

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            // Call API with searchQuery
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    const handleLogout = () => {
        try {
            // Call logout and then redirect to home page
            // We don't need to await since logout handles everything internally
            logout();
            // Redirect to home page
            history.push('/');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;

        // Navigate to the search results page
        history.push(`/search?search=${encodeURIComponent(searchQuery)}`);
    };

    // Add effect to log user state changes
    useEffect(() => {
        console.log('Auth state changed in Navbar:', {
            isAuthenticated: !!user,
            role: user?.role,
            loading: authLoading
        });
    }, [user, authLoading]);

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled
                ? 'bg-white shadow-md py-2'
                : 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3'
        }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center">
                    <div className="flex items-center flex-1">
                        <Link to="/" className={`text-xl font-bold transition-colors duration-300 ${
                            scrolled ? 'text-blue-600' : 'text-white'
                        }`}>
                            Online Card Show
                        </Link>
                        <span
                            className={`ml-3 hidden md:inline-block text-xs font-semibold px-2.5 py-1 rounded-full transition-colors duration-300 ${
                                scrolled ? 'bg-blue-100 text-blue-700' : 'bg-white/20 text-white border border-white/30'
                            }`}
                        >
                            24/7 Virtual Card Show
                        </span>
                        <button
                            className="ml-4 md:hidden flex items-center justify-center p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => setMobileMenuOpen(true)}
                            aria-label="Open menu"
                        >
                            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <div className="ml-10 hidden md:flex items-center space-x-6">
                            <div className="relative group">
                                <Link to="/listings" className={`flex items-center transition-colors duration-300 ${
                                    scrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-100'
                                }`}>
                                    <span>Explore the Show</span>
                                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </Link>
                                <div className="absolute left-0 mt-2 py-2 w-56 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 transform origin-top scale-95 group-hover:scale-100">
                                    <Link to="/listings" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        All Cards
                                    </Link>
                                    <Link to="/listings?category=Baseball" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Baseball
                                    </Link>
                                    <Link to="/listings?category=Basketball" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Basketball
                                    </Link>
                                    <Link to="/listings?category=Football" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Football
                                    </Link>
                                    <Link to="/listings?category=Hockey" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Hockey
                                    </Link>
                                    <Link to="/listings?category=Soccer" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Soccer
                                    </Link>
                                    <Link to="/listings?category=Pokemon" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Pokemon
                                    </Link>
                                    <Link to="/listings?category=Yu-Gi-Oh" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Yu-Gi-Oh
                                    </Link>
                                    <Link to="/listings?category=Magic%3A%20The%20Gathering" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Magic: The Gathering
                                    </Link>
                                    <Link to="/listings?category=Other%20Trading%20Cards" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                        Other Trading Cards
                                    </Link>
                                </div>
                            </div>

                            <Link to="/subscription-tiers" className={`transition-colors duration-300 ${
                                scrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-100'
                            }`}>
                                Vendor Pricing
                            </Link>
                        </div>
                    </div>

                    <div className="hidden md:flex items-center">
                        <Link to="/cart" className={`relative mx-4 transition-colors duration-300 ${
                            scrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-100'
                        }`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            {itemCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {itemCount}
                                </span>
                            )}
                        </Link>

                        <div className="flex items-center ml-4 space-x-4">
                            {authLoading ? (
                                <div className={`w-8 h-8 rounded-full border-2 border-t-transparent animate-spin ${
                                    scrolled ? 'border-blue-600' : 'border-white'
                                }`}></div>
                            ) : (
                                <>
                                    {!user && (
                                        <>
                                            <Link
                                                to="/signup/seller"
                                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                                                    scrolled
                                                        ? 'bg-orange-600 text-white hover:bg-orange-700 hover:shadow-lg hover:shadow-orange-200'
                                                        : 'bg-white text-orange-600 hover:bg-orange-50 hover:shadow-lg hover:shadow-orange-200'
                                                }`}
                                            >
                                                Setup Your Table
                                            </Link>
                                        </>
                                    )}
                                    {user ? (
                                        <div className="relative group">
                                            <button
                                                type="button"
                                                className={`flex items-center space-x-1 transition-colors duration-300 ${
                                                    scrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-100'
                                                }`}
                                            >
                                                {user.image_url && (
                                                    <img
                                                        src={user.image_url}
                                                        alt={user.username}
                                                        className="w-8 h-8 rounded-full mr-2 object-cover border-2 border-white"
                                                    />
                                                )}
                                                <span>{user.username || 'Account'}</span>
                                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            <div className="absolute right-0 w-56 mt-2 py-2 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 transform origin-top scale-95 group-hover:scale-100">
                                                {(user.role === 'seller' || user.role === 'Seller') ? (
                                                    <>
                                                        <Link to="/seller/dashboard" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                                            Vendor Dashboard
                                                        </Link>
                                                        <Link to="/seller/storefront" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                                            My Table
                                                        </Link>
                                                        <Link to="/offers" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                                            My Offers
                                                        </Link>
                                                        <Link
                                                            to="/seller/shipping"
                                                            className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1"
                                                        >
                                                            Shipping Settings
                                                        </Link>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Link to="/profile" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                                            My Profile
                                                        </Link>
                                                        <Link to="/orders" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                                            My Orders
                                                        </Link>
                                                        <Link to="/offers" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                                            My Offers
                                                        </Link>
                                                    </>
                                                )}
                                                {(user.role === 'admin' || user.role === 'Admin') && (
                                                    <Link to="/admin" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1">
                                                        Admin Dashboard
                                                    </Link>
                                                )}
                                                <div className="border-t border-gray-100 my-2"></div>
                                                <button
                                                    onClick={() => setShowPasswordModal(true)}
                                                    className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1"
                                                >
                                                    Change Password
                                                </button>
                                                <div className="border-t border-gray-100 my-2"></div>
                                                <button
                                                    onClick={handleLogout}
                                                    className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md mx-1"
                                                >
                                                    Logout
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex space-x-3">
                                            <Link
                                                to="/signup"
                                                className={`inline-flex items-center px-5 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                                                    scrolled
                                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                        : 'bg-white text-blue-700 hover:bg-blue-50'
                                                }`}
                                            >
                                                Enter Show
                                            </Link>
                                            <Link
                                                to="/login"
                                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                                                    scrolled
                                                        ? 'bg-gray-600 text-white hover:bg-gray-700'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                Log In
                                            </Link>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <>
                    {/* Overlay background */}
                    <div
                        className="fixed inset-0 bg-black bg-opacity-40 z-50"
                        onClick={() => setMobileMenuOpen(false)}
                        aria-label="Close menu"
                    />
                    {/* Slide-out menu */}
                    <div className="fixed top-0 left-0 w-4/5 max-w-xs h-full bg-white z-50 shadow-lg p-6 flex flex-col transition-transform duration-300 md:hidden">
                        <div className="flex items-center justify-between mb-6">
                            <span className="text-lg font-bold text-blue-700">Menu</span>
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                aria-label="Close menu"
                            >
                                <svg className="h-6 w-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        {/* Home link */}
                        <Link to="/" className="block py-2 px-2 rounded text-blue-700 font-bold hover:bg-blue-50 hover:text-blue-700 mb-2" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                        {/* Mobile search bar */}
                        <form onSubmit={handleSearch} className="mb-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search the show..."
                                    className="w-full rounded-full pl-4 pr-10 py-2 text-sm border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                />
                                <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </button>
                            </div>
                        </form>
                        <Link to="/listings" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>Explore the Show</Link>
                        <Link to="/subscription-tiers" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>Vendor Pricing</Link>
                        <Link to="/cart" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>Cart {itemCount > 0 && <span className="ml-1 bg-red-600 text-white text-xs rounded-full px-2 py-0.5">{itemCount}</span>}</Link>
                        {!authLoading && !user && (
                            <>
                                <Link to="/signup/seller" className="block py-2 px-2 rounded text-blue-700 bg-blue-100 hover:bg-blue-200 font-medium mt-2" onClick={() => setMobileMenuOpen(false)}>Setup Your Table</Link>
                                <Link to="/signup" className="block py-2 px-2 rounded text-blue-700 bg-blue-100 hover:bg-blue-200 font-medium mt-2" onClick={() => setMobileMenuOpen(false)}>Enter Show</Link>
                                <Link to="/login" className="block py-2 px-2 rounded text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium mt-2" onClick={() => setMobileMenuOpen(false)}>Log In</Link>
                            </>
                        )}
                        {!authLoading && user && (
                            <>
                                {(user.role === 'seller' || user.role === 'Seller') && (
                                    <>
                                        <Link to="/seller/dashboard" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>Vendor Dashboard</Link>
                                        <Link to="/seller/storefront" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>My Table</Link>
                                        <Link to="/offers" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>My Offers</Link>
                                        <Link to="/seller/shipping" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>Shipping Settings</Link>
                                    </>
                                )}
                                {user.role !== 'seller' && user.role !== 'Seller' && (
                                    <>
                                        <Link to="/profile" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>My Profile</Link>
                                        <Link to="/orders" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>My Orders</Link>
                                        <Link to="/offers" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>My Offers</Link>
                                    </>
                                )}
                                {(user.role === 'admin' || user.role === 'Admin') && (
                                    <Link to="/admin" className="block py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium" onClick={() => setMobileMenuOpen(false)}>Admin Dashboard</Link>
                                )}
                                <button
                                    onClick={() => { setMobileMenuOpen(false); setShowPasswordModal(true); }}
                                    className="block w-full text-left py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium mt-2"
                                >
                                    Change Password
                                </button>
                                <button
                                    onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                                    className="block w-full text-left py-2 px-2 rounded text-gray-700 hover:bg-blue-50 hover:text-blue-700 font-medium mt-2"
                                >
                                    Logout
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}

            {/* Developer tools section removed */}

            {/* Change Password Modal */}
            {showPasswordModal && (
                <div 
                    className="fixed inset-0 flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowPasswordModal(false);
                        }
                    }}
                >
                    <div className="bg-black bg-opacity-50 absolute inset-0"></div>
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
                            <button
                                onClick={() => setShowPasswordModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <ChangePasswordForm 
                            onSuccess={() => setShowPasswordModal(false)}
                            onCancel={() => setShowPasswordModal(false)}
                            showCancel={true}
                        />
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;